<template>
    <CardGenerate 
        ref="card" 
        class="card-generate-geometry"
        :app="app"
        :card="card" 
        :actionable="false" 
        @init="handleInit">

        <template #title>
            Select your model generation.
        </template>

        <template v-if="isOutage || isDegraded" #status>
            <AtomStatus 
                :level="statusLevel"
                :status="statusMessage" />
        </template>

        <template #pre>
            <div class="card-generate-geometry-pre">
                <LayoutRowEntry class="entry" :centered="true">
                    <template #first>
                        <span>Quality</span>
                    </template>
                    <template #second>         
                        <v-btn-toggle 
                            mandatory 
                            class="toggle" 
                            color="color1" 
                            elevation="0" 
                            density="default"
                            v-model="card.prompt.quality" 
                            :disabled="isOutage">
                            <v-btn 
                                class="toggle-button toggle-button-legacy" 
                                elevation="0"
                                :value="keyQualityLegacy"  
                                :disabled="!hasQualityLegacy || (!isHealthyGeometrySolid && !isAdministrator)" 
                                @click="handleClick" 
                                @mouseenter="handleHover({id: keyQualityLegacy})" 
                                @mouseleave="handleHover(null)">
                                <span>Legacy</span>
                            </v-btn>
                            <v-btn 
                                class="toggle-button" 
                                elevation="0"
                                :value="keyQualityLow"  
                                :disabled="!hasQualityLow || (isOutage && !isAdministrator)" 
                                @click="handleClick" 
                                @mouseenter="handleHover({id: keyQualityLow})" 
                                @mouseleave="handleHover(null)">
                                <v-icon icon="fa-solid fa-star-half-stroke"></v-icon>
                                <span>Low</span>
                            </v-btn>
                            <v-btn 
                                class="toggle-button" 
                                elevation="0"
                                :value="keyQualityHigh" 
                                :disabled="!hasQualityHigh || (isOutage && !isAdministrator)" 
                                @click="handleClick" 
                                @mouseenter="handleHover({id: keyQualityHigh})" 
                                @mouseleave="handleHover(null)"> 
                                <v-icon icon="fa-solid fa-star"></v-icon>
                                <span>High</span>
                            </v-btn>
                            <v-btn 
                                class="toggle-button" 
                                elevation="0"
                                :value="keyQualityUltra" 
                                :disabled="!hasQualityUltra || (isOutage && !isAdministrator)" 
                                @click="handleClick" 
                                @mouseenter="handleHover({id: keyQualityUltra})" 
                                @mouseleave="handleHover(null)"> 
                                <v-icon icon="fa-regular fa-face-grin-stars"></v-icon>
                                <span>Ultra</span>
                            </v-btn>
                        </v-btn-toggle>
                    </template>
                </LayoutRowEntry>
                <LayoutRowEntry class="entry" :centered="true">
                    <template #first>
                        <span>Texture</span>
                    </template>
                    <template #second>
                        <v-btn-toggle 
                            mandatory 
                            class="toggle" 
                            color="color1" 
                            elevation="0" 
                            density="default"
                            v-model="card.custom.texture">
                            <v-btn 
                                class="toggle-button" 
                                elevation="0"
                                :value="keyTextureOff"  
                                :disabled="!isHealthyTexture" 
                                @click="handleClick" 
                                @mouseenter="handleHover({id: keyTextureOff})" 
                                @mouseleave="handleHover(null)">
                                <v-icon icon="fa-solid fa-xmark"></v-icon>
                                <span>Off</span>
                            </v-btn>
                            <v-btn 
                                class="toggle-button" 
                                elevation="0"
                                :value="keyTextureOn"  
                                :disabled="!isHealthyTexture" 
                                @click="handleClick" 
                                @mouseenter="handleHover({id: keyTextureOn})" 
                                @mouseleave="handleHover(null)">
                                <v-icon icon="fa-solid fa-check"></v-icon>
                                <span>On</span>
                            </v-btn>
                        </v-btn-toggle>
                    </template>
                </LayoutRowEntry>
            </div>
        </template>

        <template #options>
            <OptionImage 
                v-for="option in options"
                :id="option.id"
                :index="option.index"
                :data="option.url"
                :label="option.label" 
                :compact="isExpertMode"  
                :disabled="isOutage || !hasPrivileges"
                :selected="selected === option.id"
                :selectable="option.selectable"
                @click="handleOption(option)"
                @mouseenter="handleHover(option)"
                @mouseleave="handleHover(null)">
            </OptionImage>
        </template>

        <template #post>
            <AtomText class="hide-on-mobile" :text="guidance" />
        </template>

        <template v-if="isExpertMode" #side>
        
            <SidePanelExpert>

                <div class="expert-item" style="margin-bottom:20px;">
                    <v-switch
                        inset
                        hide-details 
                        label="force newest model" 
                        color="color1" 
                        density="compact"
                        v-model="card.custom.forceNewestModel">
                    </v-switch>
                </div>

                <SideTitle title="Structure" />

                <div class="expert-item">
                    <v-select
                        label="multi-image strategy" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked" 
                        item-title="label"
                        item-value="value"
                        :items="keyStrategies" 
                        v-model="card.custom.structureMultiImageStrategy">
                    </v-select>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="steps" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        v-model="card.custom.structureSteps">
                    </v-number-input>
                </div>

                <div class="expert-item" style="display:flex;flex-direction:row;gap:10px;">
                    <v-number-input
                        label="min" 
                        color="color1" 
                        variant="outlined" 
                        control-variant="hidden" 
                        :precision="1" 
                        v-model="card.custom.structureIntervalMin">
                    </v-number-input>
                    <v-number-input
                        label="max" 
                        color="color1" 
                        variant="outlined" 
                        control-variant="hidden" 
                        :precision="1" 
                        v-model="card.custom.structureIntervalMax">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="strength" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="1" 
                        v-model="card.custom.structureStrength">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="rescale dist"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="1" 
                        v-model="card.custom.structureRescaleDist">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="rescale time"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="1" 
                        v-model="card.custom.structureRescaleTime">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="seed"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        v-model="card.custom.seed">
                    </v-number-input>
                </div>

                <SideTitle title="Geometry" />

                <div class="expert-item">
                    <v-select
                        label="multi-image strategy" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked" 
                        item-title="label"
                        item-value="value"
                        :items="keyStrategies" 
                        v-model="card.custom.geometryMultiImageStrategy">
                    </v-select>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="steps" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        v-model="card.custom.geometrySteps">
                    </v-number-input>
                </div>

                <div class="expert-item" style="display:flex;flex-direction:row;gap:10px;">
                    <v-number-input
                        label="min" 
                        color="color1" 
                        variant="outlined" 
                        control-variant="hidden" 
                        :precision="1" 
                        v-model="card.custom.geometryIntervalMin">
                    </v-number-input>
                    <v-number-input
                        label="max" 
                        color="color1" 
                        variant="outlined" 
                        control-variant="hidden" 
                        :precision="1" 
                        v-model="card.custom.geometryIntervalMax">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="strength" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="1" 
                        v-model="card.custom.geometryStrength">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="rescale dist"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="1" 
                        v-model="card.custom.geometryRescaleDist">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="rescale time"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="1" 
                        v-model="card.custom.geometryRescaleTime">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-select
                        label="solid sampling" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked" 
                        :items="[
                            keySamplingDense,
                            keySamplingOctree
                         ]" 
                        v-model="card.custom.geometrySolidSampling">
                    </v-select>
                </div>

                <div class="expert-item">
                    <v-select
                        label="solid algorithm" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked" 
                        :items="[
                            keyAlgorithmMc,
                            keyAlgorithmDmc,
                            keyAlgorithmDdmc,
                            keyAlgorithmDualc
                         ]" 
                        v-model="card.custom.geometrySolidAlgorithm">
                    </v-select>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="seed"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        v-model="card.custom.seed">
                    </v-number-input>
                </div>
            </SidePanelExpert>

        </template>

    </CardGenerate>
</template>

<script>
import QUALITY from '@generio-ai/library-legacy/constants/quality'
//import SUBSCRIPTIONS from '@generio-ai/library-legacy/constants/subscriptions'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { LayoutRowEntry } from '@generio-ai/library-legacy/components/layouts'
import CARD from '@/constants/card.js'
import OPTION from '@/constants/option.js'
import STRATEGY from '@/constants/strategy.js'
import AtomStatus from '@/components/common/atom/AtomStatus.vue'
import AtomText from '@/components/common/atom/AtomText.vue'
import CardGenerate from '@/components/flow/cards/CardGenerate.vue'
import OptionImage from '@/components/flow/options/OptionImage.vue'
import OptionIcon from '@/components/flow/options/OptionIcon.vue'
import SidePanelExpert from '@/components/flow/sides/SidePanelExpert.vue'
import SideTitle from '@/components/flow/sides/SideTitle.vue'

export default {
    name: 'CardGenerateGeometry',
    components: {
        LayoutRowEntry,
        AtomStatus,
        CardGenerate,
        OptionImage,
        OptionIcon,
        AtomText,
        SidePanelExpert,
        SideTitle
    },
    props: {
        app: {
            type: String,
            required: true
        },
        card: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            healthy: {
                model_generate_geometry_legacy: true,
                model_generate_geometry: true,
                model_generate_texture: true,
			},
            hovered: null
        }
    },
    computed: {
        ...mapGetters('auth', [
            'isAdministrator'
        ]),
        ...mapGetters('subscriptions', [
            'getSubscription'
        ]),
        ...mapGetters('flows', [
            'isExpertMode',
            'isActiveByIndex',
            'isLastByIndex',
            'getPrimaryCardForCard'
		]),

        keyQualityLegacy() {
            return QUALITY.LEGACY
        },
        keyQualityLow() {
            return QUALITY.LOW
        },
        keyQualityHigh() {
            return QUALITY.HIGH
        },
        keyQualityUltra() {
            return QUALITY.ULTRA
        },

        keyTexture() {
            return 'texture'
        },
        keyTextureOn() {
            return true
        },
        keyTextureOff() {
            return false
        },
        keyTextureDefault() {
            return this.keyTextureOff
        },

        keyStrategies() {
            return Object.values(STRATEGY)
        },
        keyStrategyDefault() {
            return STRATEGY.NONE.value
        },

        keySamplingDense() {
            return 'dense'
        },
        keySamplingOctree() {
            return 'octree'
        },
        keySamplingDefault() {
            return this.keySamplingOctree
        },

        keyAlgorithmMc() {
            return 'mc'
        },
        keyAlgorithmDmc() {
            return 'dmc'
        },
        keyAlgorithmDdmc() {
            return 'ddmc'
        },
        keyAlgorithmDualc() {
            return 'dualc'
        },
        keyAlgorithmDefault() {
            return this.keyAlgorithmDualc
        },

        hasQualityLegacy() {
            return true
        },
        hasQualityLow() {
            return true
        },
        hasQualityHigh() {
            return true
        },
        hasQualityUltra() {
            return true
        },

        hasPrivileges() {
            return true
        },

        isOutage() {
            return !this.isHealthyGeometrySolid && !this.isHealthyGeometrySurface
		},
        isDegraded() {
            return !this.isHealthyGeometrySolid || !this.isHealthyGeometrySurface || !this.isHealthyTexture
        },

        isHealthyGeometrySolid() {
            return this.healthy.model_generate_geometry_legacy || this.isAdministrator
        },
        isHealthyGeometrySurface() {
            return this.healthy.model_generate_geometry || this.isAdministrator
        },
        isHealthyTexture() {
            return this.healthy.model_generate_texture || this.isAdministrator
        },

        isQualityLegacy() {
            return this.promptQuality === QUALITY.LEGACY
        },
        isQualityLow() {
            return this.promptQuality === QUALITY.LOW
        },
        isQualityHigh() {
            return this.promptQuality === QUALITY.HIGH
        },
        isQualityUltra() {
            return this.promptQuality === QUALITY.ULTRA
        },

        isActiveCard() {
            return this.isActiveByIndex(this.app, this.index)
        },
        isLastCard() {
			return this.isLastByIndex(this.app, this.index)
		},

        statusLevel() {
            if (this.isOutage) return 'error'
            if (this.isDegraded) return 'warning'
            return 'healthy'
        },
        statusMessage() {
            if (this.isOutage) return "Model geometry generation service is currently unavailable."
            if (this.isDegraded) return "Model geometry generation service is experiencing a partial outage."
            return "Model geometry generation service is healthy."
        },

        index() {
            return this.card.index
        },
        nextIndex() {
            return this.index + 1
        },
        selected() {
            return this.card.option
        },
        primaryCard() {
            return this.getPrimaryCardForCard(this.app, this.card, false)
        },
        primaryAssets() {
            return this.primaryCard?.assets || []
        },

        prompt() {
            return this.card.prompt || {}
        },
        promptQuality() {
            return this.prompt?.quality
        },
        promptSolid() {
            return this.prompt?.solid
        },

        custom() {
            return this.card.custom || {}
        },
        customTexture() {
            return this.custom?.texture
        },
        customSeed() {
            return this.custom?.seed
        },
        customParameters() {
            return {
                'force_newest_model': this.custom?.forceNewestModel,
                // structure settings
                'structure_multi_image_strategy': this.custom?.structureMultiImageStrategy?.value,
                'structure_steps': this.custom?.structureSteps,
                'structure_guidance_interval': [
                    this.custom?.structureIntervalMin,
                    this.custom?.structureIntervalMax,
                ],
                'structure_guidance_strength': this.custom?.structureStrength,
                'structure_guidance_rescale_dist': this.custom?.structureRescaleDist,
                'structure_guidance_rescale_time': this.custom?.structureRescaleTime,
                // geometry settings
                'geometry_multi_image_strategy': this.custom?.geometryMultiImageStrategy?.value,
                'geometry_steps': this.custom?.geometrySteps,
                'geometry_guidance_interval': [
                    this.custom?.geometryIntervalMin,
                    this.custom?.geometryIntervalMax,
                ],
                'geometry_guidance_strength': this.custom?.geometryStrength,
                'geometry_guidance_rescale_dist': this.custom?.geometryRescaleDist,
                'geometry_guidance_rescale_time': this.custom?.geometryRescaleTime,
                'geometry_solid_sampling': this.custom?.geometrySolidSampling,
                'geometry_solid_algorithm': this.custom?.geometrySolidAlgorithm,
            }
        },

        options() {
            return [
                {
                    index: 1,
                    selectable: this.isHealthyGeometrySolid || this.isAdministrator,
                    ...OPTION.GENERATE_GEOMETRY_SOLID,
                },
                {
                    index: 2,
                    selectable: (this.isHealthyGeometrySurface || this.isAdministrator) && !this.isQualityLegacy,
                    ...OPTION.GENERATE_GEOMETRY_SURFACE,
                }
            ]
        },
        guidance() {
            //if (!this.hasPrivileges) 
            //    return "Ultra quality generation is available with a premium or higher subscription."

            switch (this.hovered) 
            {
                case this.keyQualityLegacy:
                    return "Fallback option to the previous model generation."
                case this.keyQualityLow:
                    return "Fast generation quality with lower level of details."
                case this.keyQualityHigh:
                    return "Balanced generation quality with good detail."
                case this.keyQualityUltra:
                    return "Best generation quality with highest level of detail."

                case this.keyTexture:
                    return "Generate a PBR-ready texture for the model."

                case OPTION.GENERATE_GEOMETRY_SOLID.id: 
                    return OPTION.GENERATE_GEOMETRY_SOLID.guidance + this.time
                case OPTION.GENERATE_GEOMETRY_SURFACE.id:
                    return OPTION.GENERATE_GEOMETRY_SURFACE.guidance + this.time
            }
            return ''
        },

        time() {
            if (this.promptQuality === QUALITY.ULTRA) return " (~45 seconds)."
            if (this.promptQuality === QUALITY.HIGH) return " (~30 seconds)."
            if (this.promptQuality === QUALITY.LOW) return " (~15 seconds)."
            if (this.promptQuality === QUALITY.LEGACY) return " (~30 seconds)."
            return "."
        },
    },
    methods: {
        ...mapActions('publics', [
			'fetchStatus'
		]),
        ...mapActions('flows', [
            'removeCard',
            'newTransaction',
            'commitPrimitiveAsset',
            'fetchGenerateModel'
        ]),
        async updateStatus() 
        {
            // default health status
            this.healthy = {
                model_generate_geometry_legacy: true,
                model_generate_geometry: true,
                model_generate_texture: true,
			}

            try
            {
                // get status
                const status = await this.fetchStatus()

                // has healthy services
				if (status?.healthy) 
				{
                    // legacy
                    const legacy = status.healthy['model_generate_geometry.legacy']
                    if (legacy === false) this.healthy.model_generate_geometry_legacy = false

                    // geometry
                    const geometry = status.healthy['model_generate_geometry']
                    if (geometry === false) this.healthy.model_generate_geometry = false

                    // texture
                    const texture = status.healthy['model_generate_texture']
                    if (texture === false) this.healthy.model_generate_texture = false
				}
            }
            catch (error)
            {
                // silent fail - if status check does not work, we still want to proceed
            }
        },
        handleInit(assets, isNew) {
            if (isNew) {
                this.card.custom ??= {}

                // card settings
                this.card.prompt.quality ??= QUALITY.DEFAULT
                this.card.custom.texture ??= this.keyTextureDefault

                // expert settings
                this.card.custom.forceNewestModel ??= false
                this.card.custom.seed ??= -1

                // expert settings: structure
                this.card.custom.structureMultiImageStrategy ??= this.keyStrategyDefault
                this.card.custom.structureSteps ??= 12
                this.card.custom.structureIntervalMin ??= 0.6
                this.card.custom.structureIntervalMax ??= 1.0
                this.card.custom.structureStrength ??= 7.5
                this.card.custom.structureRescaleDist ??= 0.7
                this.card.custom.structureRescaleTime ??= 5.0

                // expert settings: geometry
                this.card.custom.geometryMultiImageStrategy ??= this.keyStrategyDefault
                this.card.custom.geometrySteps ??= 12
                this.card.custom.geometryIntervalMin ??= 0.6
                this.card.custom.geometryIntervalMax ??= 1.0
                this.card.custom.geometryStrength ??= 7.5
                this.card.custom.geometryRescaleDist ??= 0.5
                this.card.custom.geometryRescaleTime ??= 3.0
                this.card.custom.geometrySolidSampling ??= this.keySamplingDefault
                this.card.custom.geometrySolidAlgorithm ??= this.keyAlgorithmDefault
            }
        },
        handleHover(option) {
            this.hovered = option?.id
        },
        handleClick() {
            this.$refs.card?.reset()
			this.removeCard({
				app: this.app, 
				index: this.nextIndex
			})
        },
        handleOption(option) {
            this.card.option = option.id
            
            switch(option.id) 
            {
                case OPTION.GENERATE_GEOMETRY_SOLID.id:
                    this.card.prompt.solid = true
                    this.newTransaction({
                        app: this.app,
                        card: this.card,
                        taskCommit: this.commitPrimitiveAsset(),
                        taskFetch: this.fetchGenerateModel({
                            component: CARD.MODEL,
                            quality: this.promptQuality,
                            solid: this.promptSolid,
                            texture: this.customTexture,
                            seed: this.customSeed,
                            parameters: this.customParameters
                        })
                    })
                    break

                case OPTION.GENERATE_GEOMETRY_SURFACE.id:
                    this.card.prompt.solid = false
                    this.newTransaction({
                        app: this.app,
                        card: this.card,
                        taskCommit: this.commitPrimitiveAsset(),
                        taskFetch: this.fetchGenerateModel({
                            component: CARD.MODEL,
                            quality: this.promptQuality,
                            solid: this.promptSolid,
                            texture: this.customTexture,
                            seed: this.customSeed,
                            parameters: this.customParameters
                        })
                    })
                    break
            }
        },
        handleKeyboardShortcuts(event) {

            // only if the card is active
            if (!this.isActiveCard) return

            // ignore keyboard shortcuts if the focus is on an input, textarea, select, or contenteditable element
            const target = event.target
            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target instanceof HTMLSelectElement ||
                (target instanceof HTMLElement && target.isContentEditable)
            ) {
                return
            }

            // get the pressed key
            const key = event.key
            // check if the key is a single digit (1-9)
            if (/^[1-9]$/.test(key)) {
                // convert to 0-indexed position
                const index = parseInt(key, 10) - 1
                // if an option exists at that index, trigger it
                if (index >= 0 && index < this.options.length) {
                    event.preventDefault()
                    this.handleOption(this.options[index])
                }
            }
        }
    },
    mounted() {
        this.updateStatus()
		window.addEventListener('keydown', this.handleKeyboardShortcuts)
	},
	beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeyboardShortcuts)
    }
}
</script>

<style>
.card-generate-geometry-pre .entry .second {
    width: 100%;
}

.card-generate-geometry-pre .toggle .v-btn.v-btn--disabled {
    opacity: 0.5;
}
</style>

<style scoped>
.card-generate-geometry-pre {
    width: 100%;
    padding-left: 30px;
	padding-right: 30px;
    margin-top: 30px;
    margin-bottom: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 15px;
}

.card-generate-geometry-pre .entry {
    width: 100%;
    max-width: 650px;
}

.card-generate-geometry-pre .entry .label-inactive {
    color: rgb(var(--v-theme-tertiary)) !important;
}

.card-generate-geometry-pre .entry .toggle {
    display: flex;
    width: 100%;
}

.card-generate-geometry-pre .entry .toggle-button {
    flex: 1;
}

.card-generate-geometry .toggle {
    max-height: 40px !important;
    min-height: 40px !important;
	border: 1px solid rgb(var(--v-theme-lightbase));
}

.card-generate-geometry .toggle-button {
    max-height: 38px !important;
    min-height: 38px !important;
	background-color: rgb(var(--v-theme-darkbase));
    border-radius: 0px !important;
}

.card-generate-geometry .toggle-button:not(:first-child) {
    border-left: 1px solid rgb(var(--v-theme-lightbase));
}

.card-generate-geometry .toggle-button-legacy {
    background-color: rgb(var(--v-theme-darkerbase));
}

.expert-checkbox {
    margin-bottom: 10px;
}

@media (max-width: 800px) {
    .card-generate-geometry-pre {
        padding-left: 15px;
        padding-right: 15px;
        margin-top: 20px;
        margin-bottom: 20px;
        gap: 10px;
    }
}

@media (max-width: 600px) {
    .card-generate-geometry-pre {
        margin-top: 15px;
        margin-bottom: 15px;
    }

    .card-generate-geometry-pre .entry {
        width: 100%;
        max-width: unset;
        gap: 3px !important;
    }

    .card-generate-geometry .toggle {
        max-height: 35px !important;
        min-height: 35px !important;
    }

    .card-generate-geometry .toggle-button {
        max-height: 33px !important;
        min-height: 33px !important;
    }
}

@media (max-width: 400px) {
    .card-generate-geometry-pre {
        margin-top: 10px;
        margin-bottom: 10px;
    }
}
</style>